import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { QuizQuestion as QuizQuestionType } from '../data/quizData';
import { ProgressBar } from './ProgressBar';
interface QuizQuestionProps {
  question: QuizQuestionType;
  questionIndex: number;
  totalQuestions: number;
  progress: number;
  onAnswer: (questionId: number, tag: string) => void;
}
export function QuizQuestion({
  question,
  questionIndex,
  totalQuestions,
  progress,
  onAnswer
}: QuizQuestionProps) {
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [direction, setDirection] = useState(1);
  const handleSelect = (tag: string) => {
    if (selectedTag) return;
    setSelectedTag(tag);
    setDirection(1);
    setTimeout(() => {
      setSelectedTag(null);
      onAnswer(question.id, tag);
    }, 400);
  };
  const optionLabels = ['A', 'B', 'C', 'D', 'E', 'F'];
  return <motion.div className="min-h-screen w-full flex flex-col items-center justify-center px-4 py-8" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} data-id="element-168">
      <div className="w-full max-w-xl mx-auto flex flex-col gap-8" data-id="element-169">
        {/* Progress */}
        <ProgressBar progress={progress} currentStep={questionIndex + 1} totalSteps={totalQuestions} data-id="element-170" />

        {/* Question */}
        <AnimatePresence mode="wait" custom={direction} data-id="element-171">
          <motion.div key={question.id} custom={direction} initial={{
          opacity: 0,
          x: 80 * direction
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -80 * direction
        }} transition={{
          duration: 0.2,
          ease: 'easeInOut'
        }} className="flex flex-col gap-5" data-id="element-172">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center leading-snug px-2" data-id="element-173">
              {question.question}
            </h2>

            {/* Options */}
            <div className="flex flex-col gap-3 mt-2" data-id="element-174">
              {question.options.map((option, idx) => {
              const isSelected = selectedTag === option.tag;
              return <motion.button key={option.tag} onClick={() => handleSelect(option.tag)} disabled={selectedTag !== null} initial={{
                opacity: 0,
                y: 16
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: idx * 0.06,
                duration: 0.25
              }} whileHover={selectedTag === null ? {
                scale: 1.02
              } : {}} whileTap={selectedTag === null ? {
                scale: 0.98
              } : {}} className={`
                      relative w-full text-left px-5 py-4 rounded-xl border transition-all duration-200
                      flex items-center gap-4 cursor-pointer
                      ${isSelected ? 'bg-cyan-500/15 border-cyan-400/60 shadow-[0_0_20px_rgba(0,212,255,0.15)]' : 'bg-white/[0.04] border-white/10 hover:bg-white/[0.08] hover:border-white/20'}
                      ${selectedTag !== null && !isSelected ? 'opacity-40' : ''}
                      backdrop-blur-sm
                    `} aria-label={`Opción ${optionLabels[idx]}: ${option.text}`} data-id="element-175">
                    <span className={`
                        flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold
                        transition-all duration-200
                        ${isSelected ? 'bg-cyan-400 text-black' : 'bg-white/10 text-white/50'}
                      `} data-id="element-176">
                      {optionLabels[idx]}
                    </span>
                    <span className={`text-base font-medium transition-colors duration-200 ${isSelected ? 'text-cyan-100' : 'text-white/80'}`} data-id="element-177">
                      {option.text}
                    </span>
                    {isSelected && <motion.div className="absolute right-4" initial={{
                  scale: 0,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 15
                }} data-id="element-178">
                        <svg className="w-5 h-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-179">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-180" />
                        </svg>
                      </motion.div>}
                  </motion.button>;
            })}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>;
}